import {get} from "lodash";
import {getBoolean, getInteger, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";

/**
 * Unique Redux store key associated to this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'myTickets';

// Define reducer types handled by this reducers
export const REDUCER_TYPES = {
	RESET: '@myTickets/reset',
	SET_LIST_DATA: '@myTickets/set_list_data',
	CLEAR_LIST_DATA: '@myTickets/clear_list_data',
	SET_ITEM: '@myTickets/set_item',
	CLEAR_ITEM: '@myTickets/clear_item',
	SET_ITEM_STATUS: '@myTickets/set_item_status',
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} myTicketsListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	
	setMyTicketsListData: myTicketsListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: myTicketsListData
	}),
	clearMyTicketsListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	
	/**
	 * @param {MyTicketsItemDataObject} myTicketsItem
	 * @return {{type: string, item: MyTicketsItemDataObject}}
	 */
	setMyTicketsItem: myTicketsItem => ({
		type: REDUCER_TYPES.SET_ITEM, 
		item: myTicketsItem
	}),
	clearMyTicketsItem: () => ({type: REDUCER_TYPES.CLEAR_ITEM}),

	/**
	 * @param {TicketStatus} status
	 * @return {{type: string, status: TicketStatus}}
	 */
	setMyTicketsItemStatus: status => ({type: REDUCER_TYPES.SET_ITEM_STATUS, status}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {MyTicketsItemDataObject}
	 */
	item: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};

		case REDUCER_TYPES.SET_ITEM: return {...state, item: action.item};
		case REDUCER_TYPES.CLEAR_ITEM: return {...state, item: initialState.item};
		
		case REDUCER_TYPES.SET_ITEM_STATUS:
			const item = get(state, 'item');
			return (item ? {...state, item: {...item, status: action.status}} : state);

		default: return state;
	}
};

// Selectors
export const selectors = {
	getMyTicketsList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getMyTicketsListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getMyTicketsListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'listData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getMyTicketsListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),

	getMyTicketsItem: state => get(state, [reducerStoreKey, 'item']),
};

export default reducer;